/*
 * @Description: 个人轨迹回放 — 底部播放控件
 *   播放/暂停 + 1x/2x/4x 倍速 + 进度滑块（hover 气泡）+ 跳过。
 *   悬浮在地图底部、右侧栏左侧。
 */
import React, { useRef, useState } from 'react'
import { Slider } from 'antd'
import { CaretRightOutlined, PauseOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import './index.scss'

const SPEEDS = [1, 2, 4]

interface ReplayControlProps {
  playing: boolean
  speed: number
  /** 时间轴起点（unix 毫秒） */
  rangeStart: number
  /** 时间轴终点（unix 毫秒） */
  rangeEnd: number
  /** 当前回放时刻 */
  playTime: number
  onTogglePlay: () => void
  onChangeSpeed: (speed: number) => void
  /** 拖动中（连续触发）：更新时刻并暂停播放 */
  onScrub: (timeMs: number) => void
  /** 松手（拖动结束）：停在该位置，按需恢复播放 */
  onScrubEnd: (timeMs: number) => void
  onSkip: () => void
}

const ReplayControl: React.FC<ReplayControlProps> = ({
  playing,
  speed,
  rangeStart,
  rangeEnd,
  playTime,
  onTogglePlay,
  onChangeSpeed,
  onScrub,
  onScrubEnd,
  onSkip
}) => {
  const fmt = (ms: number) => dayjs(ms).format('HH:mm')

  // 鼠标在时间轴上 hover 时，显示该位置对应时间的浮动气泡
  const trackRef = useRef<HTMLDivElement>(null)
  const [hoverTip, setHoverTip] = useState<{ x: number; time: number } | null>(
    null
  )
  const handleTrackMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = trackRef.current
    if (!el || rangeEnd <= rangeStart) return
    const rect = el.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width))
    setHoverTip({
      x: ratio * rect.width,
      time: rangeStart + ratio * (rangeEnd - rangeStart)
    })
  }
  const handleTrackLeave = () => setHoverTip(null)

  return (
    <div className="replay-control">
      <button
        type="button"
        className="replay-control__play"
        onClick={onTogglePlay}
      >
        {playing ? <PauseOutlined /> : <CaretRightOutlined />}
      </button>

      <div className="replay-control__speeds">
        {SPEEDS.map((s) => (
          <button
            key={s}
            type="button"
            className={`replay-control__speed ${
              s === speed ? 'is-active' : ''
            }`}
            onClick={() => onChangeSpeed(s)}
          >
            {s}x
          </button>
        ))}
      </div>

      <span className="replay-control__time">{fmt(rangeStart)}</span>
      <div
        className="replay-control__track"
        ref={trackRef}
        onMouseMove={handleTrackMove}
        onMouseLeave={handleTrackLeave}
      >
        {hoverTip && (
          <span
            className="replay-control__hovertip"
            style={{ left: hoverTip.x }}
          >
            {fmt(hoverTip.time)}
          </span>
        )}
        <Slider
          className="replay-control__slider"
          min={rangeStart}
          max={rangeEnd}
          value={playTime}
          step={1000}
          tooltip={{ formatter: (v) => fmt(v as number) }}
          onChange={(v) => onScrub(v as number)}
          onChangeComplete={(v) => onScrubEnd(v as number)}
        />
      </div>
      <span className="replay-control__time">{fmt(rangeEnd)}</span>

      <button type="button" className="replay-control__skip" onClick={onSkip}>
        跳过
      </button>
    </div>
  )
}

export default ReplayControl
